/**
 * Which action produced a result (DESIGN UD-29): a phrase for each kind of step, the steps from the original to a job's
 * result in order, and the lead of the overview's title that joins them. Pure functions, tested in Node.
 */
import { ACTION } from '../strings-bench.ts';
import { type DocChain, type VersionMaker, baseOf, holds } from './chain.ts';
import type { BenchJob } from './job.ts';
import type { RewriteScope } from './state.ts';

/** One step of the chain: the local pass or a deep rewrite with its scope. */
export type StepAction =
	| { kind: 'local' }
	| { kind: 'job'; scope: RewriteScope; guardrails?: true };

/** The phrase with the editorial guardrails named ("rewrite the flagged sections, guardrails on", ED-191); unchanged when off. */
export const withGuardrailsLabel = (phrase: string, on: boolean | undefined): string =>
	on ? `${phrase}, ${ACTION.guardrails}` : phrase;

/** The verb phrase for a step ("rewrite the flagged sections"; "…, guardrails on" for a run made with them). */
export function actionPhrase(step: StepAction): string {
	if (step.kind === 'local') return ACTION.local;
	const s = step.scope;
	let phrase: string;
	switch (s.kind) {
		case 'channel':
			phrase = ACTION.channel(s.id);
			break;
		case 'hotspot':
			phrase = ACTION.hotspot(s.channel);
			break;
		default:
			phrase = ACTION[s.kind];
	}
	return withGuardrailsLabel(phrase, step.guardrails);
}

/** The step a chain version records: the local pass, or the job's scope with its guardrails flag. */
export const stepOfMaker = (made: VersionMaker): StepAction =>
	made.kind === 'local'
		? { kind: 'local' }
		: { kind: 'job', scope: made.scope, ...(made.guardrails ? { guardrails: true as const } : {}) };

/** The same with a capital letter, for the job card ("Address document-wide signals"). */
export function actionTitle(step: StepAction): string {
	const p = actionPhrase(step);
	return p.charAt(0).toUpperCase() + p.slice(1);
}

/** The job's own action, or null for a record that kept no scope (a job from before scopes). */
export function jobAction(job: Partial<Pick<BenchJob, 'scope'>>): StepAction | null {
	return job.scope ? { kind: 'job', scope: job.scope } : null;
}

/**
 * The steps from the original to the job's result, oldest first: the chain's versions up to the one the job started from,
 * then the job itself. When the chain does not hold the job's base (another document, or no chain loaded), only the job's own
 * step is known.
 */
export function stepsOfJob(
	chain: DocChain | null,
	job: Pick<BenchJob, 'scope' | 'base' | 'docHash'> & Partial<Pick<BenchJob, 'guardrails'>>
): StepAction[] {
	const step = jobAction(job);
	const own: StepAction | null =
		step?.kind === 'job' && job.guardrails ? { ...step, guardrails: true } : step;
	const base = baseOf(job);
	const before: StepAction[] =
		chain && base.n > 0 && holds(chain, base)
			? chain.versions.slice(0, base.n).map((v): StepAction => stepOfMaker(v.made))
			: [];
	return own ? [...before, own] : before;
}

/** The lead of the overview's title: "Before and after both address document-wide signals and rewrite the flagged sections". */
export const overviewLead = (
	chain: DocChain | null,
	job: Pick<BenchJob, 'scope' | 'base' | 'docHash'> & Partial<Pick<BenchJob, 'guardrails'>>
): string => ACTION.before(stepsOfJob(chain, job).map(actionPhrase));
